<template>
<div class="site-header-wrapper">
  <div v-if="wideBannerHtmlLocal" v-html="wideBannerHtmlLocal"></div>
  <div id="site-header" data-qa="site-header">
    <div v-if="!!b2CUrlLocal" class="header__topmost">
      <div class="header__nav-select">
        <a :href="b2CUrlLocal" class="header__nav-select-item">{{ $res.General.ForTourists }}</a>
        <a href="/" class="header__nav-select-item header__nav-select-item--selected">{{ $res.General.ForAgencies }}</a>
      </div>
    </div>
    <div class="top">
      <div class="top__logo">
        <a class="top__logo-img" v-if="logoUrlLocal" :href="logoNavigateUrl">
          <img :src="logoUrlLocal" :alt="logoAlt">
        </a>
        <div v-else class="top__logo-img top__logo-img_loading data-waiting-animation">&nbsp;</div>
      </div>
      <div class="top__office office">
        <office-selector v-if="tourOperatorOfficeContactsLocal"
                         :location="agencyLocationLocal"
                         :language="selectedLanguage"
                         class="office__selector"
                         @tour-operator-office:change="onTourOperatorOfficeChange"
        ></office-selector>
        <div v-else class="office__selector office__selector_loading data-waiting-animation">&nbsp;</div>
        <div v-if="tourOperatorOfficeContactsLocal" class="office__all-contacts">
          <a :href="tourOperatorOfficeContactsLocal.allContactsUrl" target="_blank">
            <span>{{ $res.Views.DefaultLayout_MyLocation_AllContacts }}</span>&nbsp;
            <svg-icon icon="lh-arrow-right"></svg-icon>
          </a>
        </div>
        <div v-else class="office__all-contacts data-waiting-animation">&nbsp;</div>
      </div>
      <div class="top__contacts-exchange">
        <div v-if="tourOperatorOfficeContactsLocal" class="top__contacts contacts">
          <div v-for="(phone, ix) in tourOperatorOfficeContactsLocal.phoneNumbers"
               :key="'p'+ix"
               class="contacts__phone"
               :class="{'contacts__phone_single': tourOperatorOfficeContactsLocal.phoneNumbers.length < 2}">
            {{ phone }}
          </div>
          <a v-for="(email, ix) in tourOperatorOfficeContactsLocal.emailContacts"
            :key="'e' + ix"
            class="contacts__email"
            :href="`mailto:${email}`"
          >
            {{ email }}
          </a>
        </div>
        <div v-else class="top__contacts contacts">
          <div class="contacts__phone contacts__phone_single data-waiting-animation" style="width: 100px">&nbsp;</div>
          <div class="contacts__email data-waiting-animation" style="width: 150px">&nbsp;</div>
        </div>
        <div class="top__contacts-exchange-wrapper" v-if="!convertedAllToCurrency && currencyRateInfo">
          <div class="top__divider"></div>
          <exchange-rates :model="currencyRateInfo" class="top__exchange"/>  
        </div>
      </div>
      <div class="top__pref">
        <div class="pref__top-wrap">
          <profile-menu v-if="isAuthorized" :username="agencyUserName" :menu="userMenu" class="pref__personal"/>
          <a v-else 
             :href="loginUrl" 
             role="button" 
             class="pref__personal login-button" 
             data-qa="login-button"
          >
            {{ $res.Views.Authorization_Enter }}
          </a>
          <language-selector  v-if="Object.keys(languages).length > 1"
                              class="pref__lang"
                              :selectedLanguage="selectedLanguage"
                              :supported-languages="languages"
                              @update:selected-language="onLanguageChanged"
          />
        </div>
        <a v-if="showManageBookingLinkLocal" href="/manage-booking" class="manage-booking-button">
          <svg-icon :icon="'booking'" width="16" height="16" viewBox="0 0 16 16" />
          <span class="bold">
            {{ $res.Views.Header_ManageBooking }}
          </span>
        </a>
        <div v-if="!!feedbackUrlLocal"
             class="pref__feedback">
          <a :href="feedbackUrlLocal">{{ $res.Views.Layout_ReviewsAndSuggestions }}</a>
        </div>
      </div>
    </div>
    <div class="bottom">
      <div class="bottom__menu-wrapper">
        <template v-if="!mainMenuFetchAttemptsExceeded">
          <TopLevelMenu v-if="mainMenuLocal" class="site-header__main-menu" :menu="mainMenuLocal" prefix="main-"/>
          <div v-else class="site-header__main-menu-skeleton">
            <div class="data-waiting-animation"></div>
            <div class="data-waiting-animation"></div>
            <div class="data-waiting-animation"></div>
            <div class="data-waiting-animation"></div>
            <div class="data-waiting-animation"></div>
            <div class="data-waiting-animation"></div>
            <div class="data-waiting-animation"></div>
          </div>
        </template>
        <div v-else>
          "MMENU" cms menu is missing.
        </div>
        <template v-if="!mainMenuMyAccountFetchAttemptsExceeded">
          <TopLevelMenu v-if="mainMenuMyAccountLocal"
                         :menu="mainMenuMyAccountLocal"
                         prefix="my-acc-"
                         class="site-header__my-account-menu"
                         @top-level-disabled-item-click="onClickMyAccount"/>
          <div v-else class="site-header__my-account-menu-skeleton">
            <div class="data-waiting-animation"></div>
          </div>
        </template>
      </div>
    </div>
  </div>
  <FixedPanel v-if="showGoToB2CSuggestion">
    <div class="agency-website-banner">
      <div class="agency-website-banner-inner">
        <div class="agency-website-banner__icon">
          <svg-icon :icon="'free-icon-briefcase'" width="80" height="80" viewBox="0 0 80 80" />
        </div>
        <div class="agency-website-banner__content">
          <div class="agency-website-banner__title">{{ $res.Views.AgencyWebsiteBanner_Title }}</div>
          <div class="agency-website-banner__text">{{ $res.Views.AgencyWebsiteBanner_Default_Text }}</div>
          <div class="agency-website-banner__links">
            <a :href="b2CUrlLocal" @click.prevent="onClickTouristsButtonHandler">
              <svg-icon :icon="'holiday'" width="16" height="16" viewBox="0 0 16 16" />
              <span>{{ $res.Views.AgencyWebsiteBanner_ButtonTourists }}</span>
            </a>
            <a href="#" @click.prevent="onClickAgencyButtonHandler">
              <svg-icon :icon="'briefcase'" width="16" height="16" viewBox="0 0 16 16" />
              <span>{{ $res.Views.AgencyWebsiteBanner_ButtonAgency }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
    <template #fixed-view>
      <div class="agency-website-banner-fixed">
        <div class="agency-website-banner-fixed-inner">
          <div class="agency-website-banner-fixed__left-side">
            <span v-html="$res.Views.AgencyWebsiteBanner_Fixed_Text"></span>
            <a :href="b2CUrlLocal" @click.prevent="onClickTouristsButtonHandler">
              <span>{{ $res.Views.AgencyWebsiteBanner_Fixed_Text2 }}</span>
              <svg-icon :icon="'link'" width="16" height="16" viewBox="0 0 16 16" />
            </a>
          </div>
          <div class="agency-website-banner-fixed__right-side">
            <a href="#" @click.prevent="onClickAgencyButtonHandler">{{ $res.Views.AgencyWebsiteBanner_ButtonAgency }}</a>
          </div>
        </div>
      </div>
    </template>
  </FixedPanel>
</div>
</template>

<script>
import FixedPanel from "@/common/fixedPanel/FixedPanel.vue";
import SvgIcon from "@/common/controls/SvgIconControl.vue";
import {post} from "@/common/fetch";
import {delay, retryAsyncFunction} from "@/common/util/promises";
import LanguageSelector from "./components/LanguageSelectorComponentDesktop.vue";
import ProfileMenu from "./components/ProfileMenuComponentDesktop.vue";
import OfficeSelector from "./components/OfficeSelectorComponentDesktop.vue";
import ExchangeRates from "./components/HeaderExchangeRatesComponentDesktop.vue";
import TopLevelMenu from "./components/TopLevelMenu2ComponentDesktop.vue";
import upperFirst from "lodash/upperFirst";
import {
  setNewCultureToQuery
} from "./languageHelpers";
import YmEventsSender from "@/common/util/ymEventsSender";
import "../../styles/scroll.scss";

export default {
  inheritAttrs: false,
  components: {
    SvgIcon,
    FixedPanel,
    LanguageSelector,
    ProfileMenu,
    OfficeSelector,
    ExchangeRates,
    TopLevelMenu,
  },
  props: {
    mainMenu: Object,
    mainMenuMyAccount: Object,
    currencyRateInfo: Object,
    isAuthorized: Boolean,
    agencyUserName: String,
    agencyLocation: Object,
    loginUrl: String,
    userMenu: Object,
    tourOperatorOfficeContacts: Object,
    updateTourOperatorOfficeContactsFn: Function,
    ensureTourOperatorOfficeContactsFn: Function,
    supportedLanguages: Object,
    convertedAllToCurrency: Boolean,
    logoUrl: String,
    goToB2CSuggestion: Object,
    b2CUrl: String,
    feedbackUrl: String,
    showManageBookingLink: Boolean,
    wideBannerHtml: String,
  },
  data() {
    return {
      logoAlt: "Site Logo",
      logoNavigateUrl: "/",
      selectedLanguage: "RU",
      mainMenuLocal: null,
      b2CUrlLocal: null,
      mainMenuFetchAttemptsExceeded: false,
      mainMenuMyAccountLocal: null,
      mainMenuMyAccountFetchAttemptsExceeded: false,
      tourOperatorOfficeContactsLocal: null,
      tourOperatorOfficeContactsFetchAttemptsExceeded: false,
      agencyLocationLocal: null,
      logoUrlLocal: null,
      logoUrlFetchAttemptsExceeded: false,
      showGoToB2CSuggestion: false,
      feedbackUrlLocal: null,
      showManageBookingLinkLocal: false,
      wideBannerHtmlLocal: null,
    };
  },
  watch: {
    mainMenu: {
      handler() {
        this.mainMenuLocal = this.mainMenu;
      },
      immediate: true,
    },
    mainMenuMyAccount: {
      handler() {
        this.mainMenuMyAccountLocal = this.mainMenuMyAccount;
        this.patchMyAccountIcon();
      },
      immediate: true,
    },
    tourOperatorOfficeContacts: {
      handler() {
        this.tourOperatorOfficeContactsLocal = this.tourOperatorOfficeContacts;
      },
      immediate: true,
    },
    agencyLocation: {
      handler() {
        this.agencyLocationLocal = this.agencyLocation;
      },
      immediate: true,
    },
    logoUrl: {
      handler() {
        this.logoUrlLocal = this.logoUrl;
      },
      immediate: true,
    },
    goToB2CSuggestion: {
      handler() {
        this.showGoToB2CSuggestion = this.goToB2CSuggestion.showSuggestion;
      },
      immediate: true,
    },
    b2CUrl: {
      handler() {
        this.b2CUrlLocal = this.b2CUrl;
      },
      immediate: true
    },
    feedbackUrl: {
      handler() {
        this.feedbackUrlLocal = this.feedbackUrl;
      },
      immediate: true
    },
    showManageBookingLink: {
      handler() {
        this.showManageBookingLinkLocal = this.showManageBookingLink;
      },
      immediate: true,
    },
    wideBannerHtml: {
      handler() {
        this.wideBannerHtmlLocal = this.wideBannerHtml;
      },
      immediate: true,
    }
  },
  created() {
    if (!this.mainMenuLocal) {
      delay(1000).then(() => this.fetchMainMenu());
    }

    if (!this.mainMenuMyAccountLocal) {
      delay(1000).then(() => this.fetchMainMenuMyAccount());
    }

    if (!this.tourOperatorOfficeContactsLocal) {
      delay(1000).then(() => this.fetchTourOperatorContactsData());
    }

    this.selectedLanguage = this.$res.CurrentCulture.toUpperCase();
  },
  computed: {
    languages() {
      const {supportedLanguages = {}} = this;
      const languages = {};
      
      for (const [languageCode, item] of Object.entries(supportedLanguages)) {
        const code = languageCode.toUpperCase();
        const name = upperFirst(item.name);
        
        languages[code] = {
          icon: `/Content/Images/FlagIcons/${item.flag}.svg`,
          text: name,
          culture: item.cultureName
        };
      }

      return languages;
    }
  },
  methods: {
    onClickAgencyButtonHandler() {
      YmEventsSender.sendHotelDescriptionContinueAsAgencyFromStickyPanel();
      post("/HotelDescription/ContinueAsAgent");
      this.showGoToB2CSuggestion = false;
      window.layoutModel.goToB2CSuggestion.showSuggestion = false;
    },
    
    onClickTouristsButtonHandler({currentTarget}) {
      YmEventsSender.sendHotelDescriptionRedirectToB2cFromStickyPanel();
      const href =  currentTarget.getAttribute("href");
      window.location.assign(href);
    },
    
    onClickMyAccount() {
      window.location.assign(this.loginUrl);
    },
    
    patchMyAccountIcon() {
      if (this.mainMenuMyAccountLocal) {
        this.mainMenuMyAccountLocal.items[0].icon = "lh-my-account";
      }
    },

    fetchMainMenu() {
      retryAsyncFunction(
          10,
          1000,
          (res) => !!res.menu,
          () => post("/Layout/GetMainMenu", {}, true)
      )
          .then(res => this.mainMenuLocal = res.menu)
          .catch(() => this.mainMenuFetchAttemptsExceeded = true);
    },

    fetchMainMenuMyAccount() {
      retryAsyncFunction(
          10,
          1000,
          (res) => !!res.menu,
          () => post("/Layout/GetMainMenuMyAccount", {}, true)
      )
          .then(res => {
            this.mainMenuMyAccountLocal = res.menu;
            this.patchMyAccountIcon();
          })
          .catch(() => this.mainMenuMyAccountFetchAttemptsExceeded = true);
    },

    fetchTourOperatorContactsData() {
      if (this.ensureTourOperatorOfficeContactsFn) {
        this.ensureTourOperatorOfficeContactsFn()
          .then((contacts) => {
            this.tourOperatorOfficeContactsLocal = contacts;
            if (this.updateTourOperatorOfficeContactsFn) {
              this.updateTourOperatorOfficeContactsFn(contacts);
            }
          })
          .catch(() => this.tourOperatorOfficeContactsFetchAttemptsExceeded = true);
      }
    },

    fetchLogoUrl() {
      retryAsyncFunction(
          10,
          1000,
          (res) => !!res,
          () => post("/Layout/GetLogoUrl", {})
      )
          .then(res => {
            this.logoUrlLocal = res;
          })
          .catch(() => this.logoUrlFetchAttemptsExceeded = true);
    },

    onTourOperatorOfficeChange(vm) {
      if(vm.isSucceeded) {
        this.agencyLocationLocal = vm.agencyLocation;
        this.tourOperatorOfficeContactsLocal = vm.tourOperatorOfficeContacts;

        if (this.updateTourOperatorOfficeContactsFn) {
          this.updateTourOperatorOfficeContactsFn(vm.tourOperatorOfficeContacts);
        }
      }
    },

    onLanguageChanged(value) {
      setNewCultureToQuery(value);
    },
  }
};
</script>

<style lang="scss" scoped>
$page-width: 1128px;

a {
  color: var(--main-10);
}

#site-header {
  background-color: #fff;
}

.header__topmost {
  background-color: var(--main-10);
}

.header__nav-select {
  margin: 0 auto;
  width: $page-width;
  display: flex;
  gap: 8px;
}

.header__nav-select-item {
  padding: 8px 43px;
  color: var(--white);
  font-size: 16px;
  line-height: 24px;
  border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
  min-width: 168px;
  font-weight: 600;
  text-align: center;

  &--selected {
    background-color: var(--white);
    color: var(--second-10);
  }
}

.top {
  padding-block: 15px;
  width: $page-width;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 24px;
}

.top__logo {
  flex: 0 0 auto;
  min-width: 0;
  display: flex;
  align-items: center;
}

.top__logo-img {
  img {
    [theme="pegas"] & {
      min-height: 60px;
    }
  }
  
  img {
    [theme="hb"] & {
      width: 270px;
      height: 40px;
    }
  }

  img {
    [theme="longitours"] & {
      width: 220px;
      height: 52px;
    }
  }
  
  &.top__logo-img_loading {
    width: 164px;
  }
}

.top__office {
  flex: 0 0 auto;
  min-width: 0;
  margin-left: 16px;
}

.top__contacts-exchange {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-flow: row nowrap;
  justify-content: end;
  align-items: center;
  gap: 48px;
  position: relative;
}

.top__contacts {
  flex: 0 0 auto;
}

.top__divider {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--main-40);


}

.top__exchange {
  flex: 0 0 auto;
  padding-left: 48px;
}

.top__pref {
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: end;
  flex: 0 0 auto;
  min-width: 0;
}

.office {
}

.office__selector {
  &.office__selector_loading {
    width: 150px;
  }
}

.office__all-contacts {
  margin-top: 4px;
  padding-inline-start: 20px;

  [dir="rtl"] & {
    svg {
      transform: rotate(180deg);
    }
  }
  
  a {
    &:hover {
      border-bottom: 1px solid currentColor;

      [theme="hb"] & {
        color: var(--green-1) !important;
      }
    }

    svg {
      position: relative;
      top: -1px;
    }
  }
}

.contacts {
  white-space: nowrap;
}

.contacts__phone {
  &.contacts__phone_single {
    font-size: 16px;
    line-height: 24px;
  }

  font-weight: 600;
}

.contacts__email {
  &:hover {
    color: var(--hover);
  }
}

.pref {
  display: flex;
  flex-flow: row wrap;
}

.pref__top-wrap {
  display: flex;
  flex-flow: row nowrap;
  gap: 24px;
  margin-inline-start: auto;
}

.pref__personal {
  flex: 0 0 auto;
  margin-inline-start: auto;
}

.pref__lang {
  flex: 0 0 auto;
}

.pref__feedback {
  flex: 1 1 100%;
  text-align: end;
  white-space: nowrap;

  [theme="hb"] & {
    display: none;
  }

  a {
    color: var(--main-10);
    &:hover {
      border-bottom: 1px solid currentColor;
    }
  }
}

.login-button {
  color: var(--main-10);
  background: transparent;
  border: none;
  height: 20px;

  &:hover {
    color: var(--hover);
  }
}

.manage-booking-button {
  display: flex;
  gap: 8px;
  align-items: center;
  height: 20px;

  &:hover {
    color: var(--hover);
  }
}

.bottom {
  border-top: 2px solid var(--main-40);
  padding-block: 10px;
}

.bottom__menu-wrapper {
  width: $page-width;
  margin: 0 auto;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
}

.site-header__main-menu-skeleton,
.site-header__my-account-menu-skeleton {
  display: flex;
  flex-flow: row nowrap;
  gap: 24px;

  > * {
    height: 20px;
    width: 100px;
    border-radius: 4px;
  }
}

.agency-website-banner,
.agency-website-banner-fixed {
  background-color: var(--status-few-30);
}

.agency-website-banner-inner {
  display: flex;
  align-items: center;
  gap: 23px;
  padding: 20px 0;
  width: 1128px;
  margin: 0 auto;
}

.agency-website-banner__links {
  display: flex;
  gap: 24px;

  a {
    display: flex;
    gap: 8px;
    align-items: center;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }
}

.agency-website-banner__content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.agency-website-banner__icon {
  svg {
    color: var(--status-few-20);
  }
}

.agency-website-banner__title {
  font-size: 24px;
  font-weight: 600;
  line-height: 38px;
}

.agency-website-banner__text {
  font-size: 16px;
  line-height: 24px;
}

.agency-website-banner-fixed-inner {
  width: 1128px;
  margin: 0 auto;
  padding: 10px 0;
  display: flex;
  gap: 24px;
}

.agency-website-banner-fixed__left-side {
  display: flex;
  gap: 4px;
  flex-grow: 1;

  a {
    display: flex;
    gap: 8px;
    align-items: center;
    font-weight: 600;
  }
}

.agency-website-banner-fixed__right-side {
  a {
    font-weight: 600;
  }
}
</style>

<style>
.header-wide-banner {
  position: relative;
  height: 110px;
  overflow: hidden;
}

.header-wide-banner__link,
.header-wide-banner__image {
  display: block;
}

.header-wide-banner__link {
  height: 100%;
}

.header-wide-banner__image {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
